import { Page, PageNotFound } from "@/components";
import { TeamHero } from "./components/TeamHero";
import {
  Card,
  Container,
  Flex,
  Skeleton,
  SkeletonHeading,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@suns/design-system";
import { useAccount, useTeam } from "@/shared/hooks";
import { useParams } from "react-router-dom";
import { SunsApiError } from "@suns/api";
import { EntityIntelListings } from "../../components/EntityIntelListings/EntityIntelListing";
import { TeamStats } from "./components/TeamStats";

import { TeamRoster } from "./components/TeamRoster";
import { TeamAnalytics } from "./components/TeamAnalytics";
import { TeamPicks } from "./components/TeamPicks";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { TeamRow } from "@suns/api/generated-client/apollo";

export function TeamsView() {
  const { teamId } = useParams();
  const { team, loading, error } = useTeam(Number(teamId));
  const { hasScoutAdminAccess, hasDeveloperAccess } = useAccount();
  const tabOptions: string[] = ["Roster", "Stats"];

  if (hasScoutAdminAccess) {
    tabOptions.push("Intel");
    tabOptions.splice(1, 0, "Analytics"); // Insert at index 1
  }

  if (hasDeveloperAccess) {
    tabOptions.push("Picks");
  }

  return (
    <Page
      title={loading ? "..." : team?.fullName ?? "View Team"}
      loading={loading}
      loadingState={<TeamViewLoading />}
      size="sm"
      render={() => {
        if (error) {
          throw new SunsApiError("Error loading team", { cause: error });
        }

        if (!team || !teamId) {
          return <PageNotFound />;
        }

        return (
          <Container size="xl">
            <Flex direction="down" gap="md">
              <TeamHero team={team} />
              <Card>
                <Tabs defaultValue={tabOptions[0]}>
                  {tabOptions.length && (
                    <TabsList className="mb-4">
                      {tabOptions.map((tab) => (
                        <TabsTrigger key={tab} value={tab}>
                          {tab}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  )}
                  <TabsContent value="Analytics">
                    <TeamAnalytics team={team} />
                  </TabsContent>
                  <TabsContent value="Roster">
                    <TeamRoster
                      teamId={teamId}
                      leagueId={team?.domesticLeagueId}
                    />
                  </TabsContent>
                  <TabsContent value="Stats">
                    <TeamStats
                      teamNbaId={team?.nbaId}
                      teamDomesticLeagueId={team?.domesticLeagueId}
                    />
                  </TabsContent>
                  <TabsContent value="Picks">
                    <TeamPicks team={team as TeamRow} />
                  </TabsContent>
                  <TabsContent value="Intel">
                    <EntityIntelListings />
                  </TabsContent>
                </Tabs>
              </Card>
            </Flex>
          </Container>
        );
      }}
    />
  );
}

function TeamViewLoading() {
  return (
    <Container size="xl">
      <Flex direction="down" gap="md">
        <Card>
          {/* Desktop only */}
          <Flex gap="md" align="center" className="max-md:hidden">
            <Skeleton className="h-36 w-36 rounded-full" />
            <Flex direction="down" gap="md">
              <Skeleton className="h-10 w-48" />
              <Flex gap="xl">
                <Flex direction="down" gap="sm">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-8 w-32" />
                </Flex>
                <Flex direction="down" gap="sm">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-8 w-32" />
                </Flex>
                <Flex direction="down" gap="sm">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-8 w-32" />
                </Flex>
                <Flex direction="down" gap="sm">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-8 w-32" />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
          {/* Mobile only */}
          <Flex gap="md" direction="down" className="md:hidden">
            <Flex gap="sm" align="center">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-6 w-48" />
            </Flex>
            <Flex gap="lg">
              <Flex direction="down" gap="sm">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-16" />
              </Flex>
              <Flex direction="down" gap="sm">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-16" />
              </Flex>
              <Flex direction="down" gap="sm">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-36" />
              </Flex>
              <Flex direction="down" gap="sm">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-16" />
              </Flex>
            </Flex>
          </Flex>
        </Card>
        <Card>
          <Flex className="w-full max-w-4xl" direction="down" gap="lg">
            <Flex direction="down" gap="md">
              <SkeletonHeading size="3xl" className="w-36" />
            </Flex>
          </Flex>
        </Card>
      </Flex>
    </Container>
  );
}
